import {useEffect, useRef} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";

import {stripSessionEndedSearchParams} from "@/src/modules/auth/lib/session-ended-copy";
import {useStoredSession} from "@/src/modules/auth/store/session";
import {useMe} from "@/src/modules/core/hooks";
import {
  getDispatchPostLoginRoute,
  isDispatchOperatorRole,
} from "@/src/modules/core/lib/dispatch-center-policy";
import {hydrateResumableFormDraft} from "@/src/modules/core/lib/form-draft-boot";
import {loadActiveResumableFormDraft} from "@/src/modules/core/lib/form-draft";
import {shiftsService} from "@/src/modules/shifts/services";

import {shouldEnforcePropertySetupGate} from "../lib/me-requires-properties";

/** Auth entry URLs — not real deep links; post-login should use role default (e.g. /home). */
const POST_LOGIN_ENTRY_PATHS = new Set(["/", "/login", "/onelogin", "/forgot", "/reset"]);

function isPostLoginDeepLink(pathname: string, searchParams: URLSearchParams): boolean {
  if (POST_LOGIN_ENTRY_PATHS.has(pathname)) {
    return searchParams.get("_push") === "1";
  }
  return true;
}

export default function useRedirectAfterMe() {
  const [searchParams] = useSearchParams();
  const session = useStoredSession();
  /** Last user we ran post-login redirect for; reset only on logout (see effect). */
  const redirectedForUserId = useRef<number | null>(null);
  const {data: me} = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const postLoginSearchParams = stripSessionEndedSearchParams(searchParams);
  const queryString = postLoginSearchParams.toString();

  useEffect(() => {
    if (!session) {
      redirectedForUserId.current = null;
      return;
    }

    // Do not clear redirect ref when `me` is briefly undefined during background /me refetch
    // (e.g. after invalidateQueries) — that re-ran post-login navigate and bounced routes.
    if (!me) {
      return;
    }

    if (redirectedForUserId.current === me.user_id) {
      return;
    }

    redirectedForUserId.current = me.user_id;

    const resumableDraft = loadActiveResumableFormDraft();
    if (resumableDraft) {
      const meProperties = me.properties ?? [];
      if (meProperties.length > 0) {
        hydrateResumableFormDraft(resumableDraft, meProperties, me);
      }
      const params = queryString;
      let route = resumableDraft.route;
      if (params) {
        route += `?${params}`;
      }
      navigate(route, {replace: true, state: {fromMe: true}});
      return;
    }

    const params = queryString;
    const state = {state: {fromMe: true}};

    // Impersonation swaps user_id — never preserve support/admin deep links (e.g. /support/view-as-user).
    if (me.impersonation?.active) {
      let route = "";
      if (me.isResident) {
        route = "/home";
      } else if (isDispatchOperatorRole(me)) {
        route = getDispatchPostLoginRoute(me);
      } else if (shiftsService.isShiftsHomeRole(me)) {
        route = "/home";
      } else if (shouldEnforcePropertySetupGate(me)) {
        route = "/properties";
      } else {
        route = me.isAdmin ? "/dashboard" : "/dars";
      }
      if (params) {
        route += `?${params}`;
      }
      navigate(route, {replace: true, ...state});
      return;
    }

    if (isPostLoginDeepLink(location.pathname, postLoginSearchParams)) {
      const route = params ? `${location.pathname}?${params}` : location.pathname;
      navigate(route, state);
      return;
    }

    let route = "";
    if (me.isResident) {
      route = "/home";
    } else if (isDispatchOperatorRole(me)) {
      route = getDispatchPostLoginRoute(me);
    } else if (shiftsService.isShiftsHomeRole(me)) {
      route = "/home";
    } else if (shouldEnforcePropertySetupGate(me)) {
      route = "/properties";
    } else {
      const defaultLanding = POST_LOGIN_ENTRY_PATHS.has(location.pathname)
        ? me.isAdmin
          ? "/dashboard"
          : "/dars"
        : location.pathname;
      route = defaultLanding;
    }

    if (params) {
      route += `?${params}`;
    }
    navigate(route, state);
  }, [session, me?.user_id, me?.properties, navigate, location.pathname, queryString]);
}
